import { inject, injectable, multiInject } from '@patsnap/tahiti-core/shared/inversify';
import { SpecificationSection } from '../specification-section';
import { SpecificationContentType, SpecificationStep, type SpecificationParagraphContentType } from '../../enums';
import { AbstractBaseSection } from '../sections/abstract-base-section';
import { StreamStatus } from '@/services/sse-ps-http/enums';
import { SpecificationGenContentOrder, SpecificationAllContentOrder } from '../../constants/specification-gen-content';
import { GetTaskDetail } from '@/infrastructure/api-functions/core-drafting-api/get-task-detail-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { Logger } from '@patsnap/tahiti-core';
import { TaskStatus } from '@/domains/task/enums';

@injectable()
export class RootSection extends AbstractBaseSection {
    readonly contentType = 'RootSection';

    constructor(
        @multiInject(SpecificationSection)
        private _sections: SpecificationSection[],
        @inject(GetTaskDetail)
        private _getTaskDetail: GetTaskDetail<SpecificationParagraphContentType, SpecificationContentType>,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
        @inject(Logger)
        private readonly logger: Logger,
    ) {
        super();
    }

    getSectionByType(sectionType: SpecificationContentType): SpecificationSection {
        return this._sections.find(section => section.contentType === sectionType)!;
    }

    private _currentSection: SpecificationSection | undefined;

    async generate(regenerate?: boolean) {
        this._clearState();
        let currentContent = this.updateContent({
            type: this.contentType,
            status: StreamStatus.PROGRESS_WITHOUT_DATA,
            content: '',
            children: [],
        });
        let contentTypes = [...SpecificationGenContentOrder];

        const { step_detail, task_status } = await this._getTaskDetail.getTaskDetail(this._currentTaskService.taskId);

        if (!regenerate) {
            const paragraphs = step_detail[SpecificationStep.GenContent] || [];
            if (paragraphs.length <= 0) {
                regenerate = true;
            } else {
                const processedParagraphs = paragraphs.map(paragraph => paragraph.content_type);
                // 历史数据中的不再使用的段落后端依然会返回，在此依然支持
                // 动态加入的段落也会返回，动态加入的段落不在排序内，在此排除掉
                contentTypes = SpecificationAllContentOrder.reduce((acc, type) => {
                    if (processedParagraphs.includes(type)) {
                        acc.push(type);
                    }
                    return acc;
                }, [] as SpecificationParagraphContentType[]);
            }
        }

        for (const contentType of contentTypes) {
            const sectionService = this.getSectionByType(contentType);
            if (sectionService) {
                const idx = currentContent.children!.length;
                this._currentSection = sectionService;
                const provider = sectionService.execute(regenerate);

                try {
                    await provider.subscribe((paragraph) => {
                        const children = currentContent.children || [];
                        children[idx] = paragraph;
                        currentContent = this.updateContent({
                            ...currentContent,
                            children,
                            status: StreamStatus.PROCESSING,
                        });
                    }).promise;
                } catch (err: any) {
                    currentContent = this.updateContent({
                        ...currentContent,
                        status: StreamStatus.FAILED,
                    });
                    this.logger.error(`failed: ${err?.message}`);
                    throw err;
                }

                this.logger.info(`finish: ${contentType}`);
            } else {
                this.logger.warn(`No section service found for content type: ${contentType}`);
            }
        }

        const allFinish = !(!regenerate && task_status !== TaskStatus.COMPLETED);

        this.updateContent({
            ...this.currentContent!,
            status: allFinish ? StreamStatus.FINISH : StreamStatus.FAILED,
        });
    }

    private _clearState() {
        if (this._currentSection) {
            this._currentSection.stop();
            this._currentSection = undefined;
        }
    }

    stop() {
        this._clearState();
        this.updateContent({
            ...this.currentContent!,
            status: StreamStatus.ABORTED,
        });
    }
}
